import { useState, useEffect } from "react";
import { useRoute } from "wouter";
import { motion } from "framer-motion";
import { Star, MapPin, Phone, Video, CheckCircle, Clock, Award, GraduationCap, Languages, Building2, ArrowLeft, Plus, Calendar, Mail, FileText, Activity, Heart, ChevronLeft, ChevronRight, Share, ChevronDown, ChevronUp, Globe, Link2, Bell, Shield, Briefcase, Calculator, MessageSquare, HelpCircle, Send, Loader2, Stethoscope, Lock, LogIn, Eye, Package, Bookmark, LayoutGrid, List, Facebook, Instagram, BookOpen } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  useGetProvider,
  useAddCareTeamMember,
  useListCareTeam,
  useRemoveCareTeamMember,
  getListCareTeamQueryKey,
  useListMasterCptCodes,
  useCreateCostEstimate,
  useBookAppointment,
  useStartAppointment,
  useInviteProviderToPortal,
  useGetProviderReviews,
  useGetSavedBundleIds,
  useToggleSavedBundle,
  API_BASE
} from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Search, CreditCard } from "lucide-react";
import QuickBundleCheckoutModal from "@/components/QuickBundleCheckoutModal";

export default function ProviderDetail() {
  const [match1, params1] = useRoute("/provider/:id");
  const [match2, params2] = useRoute("/provider/:name/:id");
  const [match3, params3] = useRoute("/member/providers/:id");
  const [match4, params4] = useRoute("/member/providers/:name/:id");
  const [match5, params5] = useRoute("/providers/:id");
  const [match6, params6] = useRoute("/providers/:name/:id");
  const id = (params2 as any)?.id || (params1 as any)?.id || (params4 as any)?.id || (params3 as any)?.id || (params6 as any)?.id || (params5 as any)?.id || "";
  const { data: provider, isLoading } = useGetProvider(id, { query: { enabled: !!id, queryKey: ["provider", id] } });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const addToTeam = useAddCareTeamMember();
  const removeFromTeam = useRemoveCareTeamMember();
  const { data: careTeamData } = useListCareTeam();
  const careTeam = careTeamData?.items || [];
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try {
        setUser(JSON.parse(u));
      } catch (e) {
        console.error("Error parsing user from localStorage", e);
      }
    }
  }, []);


  const isProviderViewer = Boolean(
    (user && (
      user.role?.toLowerCase() === 'provider' ||
      user.userType?.toLowerCase() === 'provider' ||
      !!user.provider ||
      !!user.npi
    )) ||
    (user && provider && (
      String(user.id) === String(provider.id) ||
      String(user.provider?.id) === String(provider.id) ||
      String(user.id) === String(id) ||
      String(user.provider?.id) === String(id)
    )) ||
    (typeof window !== "undefined" && (
      window.location.search.includes("preview=true") ||
      window.location.search.includes("from=settings")
    ))
  );

  const isInCareTeam = careTeam?.some((member: any) => member.id === provider?.id);
  const [showBooking, setShowBooking] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdVerificationCode, setCreatedVerificationCode] = useState<string | null>(null);
  const [showEstimatorModal, setShowEstimatorModal] = useState(false);
  const [estimatePatientNotes, setEstimatePatientNotes] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitePatientName, setInvitePatientName] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [locationViewMode, setLocationViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedLocations, setExpandedLocations] = useState<Record<number, boolean>>({ 0: true });

  const toggleLocationExpand = (idx: number) => {
    setExpandedLocations((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const [bundleViewMode, setBundleViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedBundles, setExpandedBundles] = useState<Record<string | number, boolean>>({});

  const toggleBundleExpand = (bId: string | number) => {
    setExpandedBundles((prev) => ({ ...prev, [bId]: !prev[bId] }));
  };
  const inviteProviderMutation = useInviteProviderToPortal();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCptCodes, setSelectedCptCodes] = useState<any[]>([]);
  const { data: masterCptCodes } = useListMasterCptCodes();
  const createEstimate = useCreateCostEstimate();
  const { data: savedBundleIds = [] } = useGetSavedBundleIds();
  const toggleSavedBundle = useToggleSavedBundle();

  const handleToggleWishlist = async (bundleId: string, bundleName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await toggleSavedBundle.mutateAsync(bundleId);
      toast({
        title: res.saved ? "Saved to Wishlist" : "Removed from Wishlist",
        description: res.message || `"${bundleName}" updated in your saved bundles.`
      });
    } catch (err: any) {
      toast({
        title: "Wishlist Error",
        description: err.message || "Failed to update saved bundles",
        variant: "destructive"
      });
    }
  };

  const [selectedTime, setSelectedTime] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [patientType, setPatientType] = useState<"new" | "existing">("new");
  const [visitReason, setVisitReason] = useState("");
  const [selectedGroupProvider, setSelectedGroupProvider] = useState("any");
  const [notes, setNotes] = useState("");
  const [insurance, setInsurance] = useState("I'm paying out of pocket");
  const [checkoutBundle, setCheckoutBundle] = useState<any>(null);

  const bookAppointment = useBookAppointment();

  const [reviewFilter, setReviewFilter] = useState<'all' | 'individual' | 'group'>('all');
  const { data: reviewsResponse } = useGetProviderReviews(provider?.id, reviewFilter, 1, 10, { query: { enabled: !!provider?.id } });
  const realReviews = reviewsResponse?.data?.items || [];
  const avgRating = reviewsResponse?.data?.averageRating || provider?.rating || 0;
  const reviewCount = reviewsResponse?.data?.reviewCount || provider?.reviewCount || 0;

  const handlePrevDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(prev);
    setSelectedTime("");
  };

  const handleNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    setSelectedDate(next);
    setSelectedTime("");
  };

  const handleSendInvitation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!detail?.provider?.id && !detail?.id) return;
    const targetId = detail.provider?.id || detail.id;

    inviteProviderMutation.mutate({
      id: targetId,
      patientName: invitePatientName.trim() || undefined,
      message: inviteNote.trim() || undefined
    }, {
      onSuccess: () => {
        toast({
          title: "Invitation Sent!",
          description: `We've sent an invitation to ${detail?.name || 'the provider'} to join StraightDoc.`
        });
        setShowInviteModal(false);
        setInvitePatientName("");
        setInviteNote("");
      },
      onError: (err: any) => {
        toast({
          variant: "destructive",
          title: "Invitation Failed",
          description: err.message || "Could not send invitation. Please try again."
        });
      }
    });
  };

  const handleToggleTeam = () => {
    if (!provider) return;
    if (isInCareTeam) {
      removeFromTeam.mutate(
        { id: provider.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCareTeamQueryKey() });
            toast({ title: "Removed from care team", description: `${provider.firstName} ${provider.lastName} has been removed from your care team.` });
          },
          onError: (error: any) => {
            toast({ variant: "destructive", title: "Error", description: error.message || "Failed to remove from care team" });
          }
        }
      );
    } else {
      addToTeam.mutate(
        { providerId: provider.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCareTeamQueryKey() });
            toast({ title: "Added to care team", description: `${provider.firstName} ${provider.lastName} has been added to your care team.` });
          },
          onError: (error: any) => {
            toast({ variant: "destructive", title: "Error", description: error.message || "Failed to add to care team" });
          }
        }
      );
    }
  };

  const renderHeader = () => (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800">
      <div className="w-full max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link href="/">
          <div className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-sm">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white tracking-tight">StraightDoc</span>
          </div>
        </Link>
        <div className="flex items-center gap-4">
          {user ? (
            <div className="flex items-center gap-5 mr-1">
              <button className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors">
                <Bell className="w-5 h-5" />
              </button>
              <Link href="/member/settings">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-sm shadow-sm ring-[3px] ring-blue-50 dark:ring-blue-900/30 cursor-pointer hover:scale-105 transition-transform">
                  {user.name ? user.name.split(" ").map((n: string) => n[0]).slice(0, 2).join("").toUpperCase() : "U"}
                </div>
              </Link>
            </div>
          ) : (
            <>
              <Link href="/login">
                <Button variant="ghost" className="font-semibold text-slate-600 dark:text-slate-300 hidden sm:flex">Log In</Button>
              </Link>
              <Link href="/register">
                <Button className="font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm">Sign Up</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );

  if (isLoading) return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {renderHeader()}
      <main className="pt-24 p-6 w-full max-w-[1500px] mx-auto">
        <Skeleton className="h-6 w-20 mb-6" />
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex gap-6"><Skeleton className="w-24 h-24 rounded-2xl" /><div className="flex-1"><Skeleton className="h-7 w-48 mb-2" /><Skeleton className="h-5 w-32 mb-4" /><Skeleton className="h-4 w-64" /></div></div>
        </div>
      </main>
    </div>
  );

  if (!provider) return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {renderHeader()}
      <main className="pt-24 p-6 text-center w-full max-w-[1500px] mx-auto">
        <p className="text-muted-foreground mb-4">Provider not found.</p>
        <Link href="/member/search"><Button variant="outline">Back to search</Button></Link>
      </main>
    </div>
  );

  const detail = provider as typeof provider & {
    about?: string;
    education?: { institution: string; degree: string; year: number }[];
    certifications?: string[];
    hospitalAffiliations?: string[];
    reviews?: { id: number; patientName: string; rating: number; comment: string; date: string }[];
    availability?: { date: string; slots: string[] }[];
    languages?: string[];
    addresses?: { id: string, buildingNo: string, street1: string, street2: string, city: string, state: string, zipCode: string, isPrimary: boolean }[];
    bundles?: { id: string, name: string, description: string, standardPrice: number, status: string }[];
    credentials?: any[];
  };

  const isProviderAssociated = detail?.provider?.isAssociated !== false && (detail as any)?.isAssociated !== false;

  const gradient = "from-indigo-500 to-cyan-500";
  const initials = (detail?.name || "Provider").split(" ").filter(Boolean).map((n: string) => n[0]).slice(0, 2).join("");
  const isGroup = detail.practiceType?.toLowerCase() === 'group' || detail.practiceType?.toLowerCase() === 'clinic';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Custom Member Header */}
      {renderHeader()}

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 xl:px-10 w-full max-w-[1500px] mx-auto">
        <div className="space-y-8">

            {/* Header Layout Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 md:p-7 shadow-xs mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                
                {/* Left: Profile Photo/Avatar & Main Header Info */}
                <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-center flex-1 min-w-0">
                  {detail.profilePicture ? (
                    <img src={`${API_BASE.replace(/\/v1|\/api/, '')}${detail.profilePicture}`} alt={detail.name} className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover flex-shrink-0 shadow-md ring-4 ring-slate-100 dark:ring-slate-800" />
                  ) : (
                    <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-2xl md:text-3xl font-black flex-shrink-0 shadow-md ring-4 ring-slate-100 dark:ring-slate-800`}>
                      {initials}
                    </div>
                  )}

                  <div className="space-y-2 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{detail.name}</h1>
                      {detail.accepting && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-bold rounded-full text-xs border border-emerald-200/60 dark:border-emerald-800/60">
                          <CheckCircle className="w-3.5 h-3.5" /> Accepting Patients
                        </span>
                      )}
                    </div>

                    <p className="text-sm md:text-base text-indigo-600 dark:text-indigo-400 font-bold">
                      {!isGroup ? (detail.headline || detail.specialty) : (detail.practiceType || "Medical Group")}
                    </p>

                    {/* Compact Metadata Row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-600 dark:text-slate-400 font-medium pt-0.5">
                      <div className="flex items-center gap-1">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map(s => (
                            <Star key={s} className={`w-3.5 h-3.5 ${s <= avgRating ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200 dark:fill-slate-700 dark:text-slate-700"}`} />
                          ))}
                        </div>
                        <span className="font-bold text-slate-900 dark:text-white ml-0.5">{avgRating.toFixed(1)}</span>
                        <span className="text-slate-500 font-medium">({reviewCount} reviews)</span>
                      </div>

                      {detail.addresses && detail.addresses.length > 0 && (() => {
                        const addrParts = [
                          detail.addresses[0].street1,
                          detail.addresses[0].city,
                          detail.addresses[0].state,
                          detail.addresses[0].zipCode
                        ].filter((part: any) => part && String(part).trim() !== "");
                        return addrParts.length > 0 ? (
                          <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400 font-medium">
                            <MapPin className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                            <span>{addrParts.join(", ")}</span>
                          </div>
                        ) : null;
                      })()}

                      <div className="flex flex-wrap items-center gap-4 text-slate-600 dark:text-slate-400 font-medium">
                        {(provider.provider?.individualNpiNumber || provider.individualNpiNumber || provider.provider?.npiNumber) && (
                          <div className="flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                            <span>Ind. NPI: <strong className="font-semibold text-slate-800 dark:text-slate-200">{provider.provider?.individualNpiNumber || provider.individualNpiNumber || provider.provider?.npiNumber}</strong></span>
                          </div>
                        )}
                        {(provider.provider?.groupNpiNumber || provider.groupNpiNumber) && (
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                            <span>Group NPI: <strong className="font-semibold text-slate-800 dark:text-slate-200">{provider.provider?.groupNpiNumber || provider.groupNpiNumber}</strong></span>
                          </div>
                        )}
                        {provider.provider?.facilityType && (
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                            <span>Facility: <strong className="font-semibold text-slate-800 dark:text-slate-200">{provider.provider?.facilityType}</strong></span>
                          </div>
                        )}
                        {(provider.provider?.affiliatedName || provider.affiliatedName) && (
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                            <span>Affiliated Org: <strong className="font-semibold text-slate-800 dark:text-slate-200">{provider.provider?.affiliatedName || provider.affiliatedName}</strong></span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Platform Directory Badges */}
                    {(() => {
                      const prov = provider.provider;
                      const links: { platform: string; url: string }[] = [];
                      if (Array.isArray(prov?.socialLinks) && prov.socialLinks.length > 0) {
                        prov.socialLinks.forEach((l: any) => {
                          if (l && l.url) links.push({ platform: l.platform || 'Link', url: l.url });
                        });
                      } else {
                        if (prov?.zocdocUrl) links.push({ platform: "Zocdoc", url: prov.zocdocUrl });
                        if (prov?.webmdUrl) links.push({ platform: "WebMD", url: prov.webmdUrl });
                        if (prov?.healthgradesUrl) links.push({ platform: "Healthgrades", url: prov.healthgradesUrl });
                        if (prov?.usnewsUrl) links.push({ platform: "US News", url: prov.usnewsUrl });
                      }

                      if (links.length === 0) return null;

                      return (
                        <div className="flex flex-wrap items-center gap-2 pt-1.5">
                          {links.map((item, idx) => {
                            let icon = <Globe className="w-3.5 h-3.5" />;
                            const name = (item.platform || "Link").toLowerCase();
                            if (name.includes("facebook")) icon = <Facebook className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />;
                            else if (name.includes("instagram")) icon = <Instagram className="w-3.5 h-3.5 text-pink-600 dark:text-pink-400" />;
                            else if (name.includes("zocdoc")) icon = <Calendar className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />;
                            else if (name.includes("webmd")) icon = <Activity className="w-3.5 h-3.5 text-orange-500" />;
                            else if (name.includes("healthgrades")) icon = <Star className="w-3.5 h-3.5 text-amber-500" />;
                            else if (name.includes("blog")) icon = <BookOpen className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />;

                            return (
                              <a
                                key={idx}
                                href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold transition-all bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 shadow-2xs"
                              >
                                {icon} {item.platform}
                              </a>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Right: Three Header Buttons Column */}
                <div className="flex flex-col items-stretch sm:items-end gap-2.5 shrink-0 w-full sm:w-auto pt-4 lg:pt-0 border-t lg:border-t-0 border-slate-100 dark:border-slate-800">
                  <Button
                    className={`h-10 px-4 w-full sm:w-48 justify-center rounded-xl font-bold text-xs transition-all shadow-xs ${isInCareTeam ? 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                    onClick={handleToggleTeam}
                    disabled={addToTeam.isPending || removeFromTeam.isPending}
                  >
                    {isInCareTeam ? (
                      <><CheckCircle className="w-4 h-4 mr-1.5 text-indigo-600 dark:text-indigo-400" /> {removeFromTeam.isPending ? "Removing..." : "In Care Team"}</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-1.5" /> {addToTeam.isPending ? "Adding..." : "Add to Care Team"}</>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    className="h-10 px-4 w-full sm:w-48 justify-center rounded-xl font-bold text-xs bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors shadow-2xs"
                    onClick={() => setShowEstimatorModal(true)}
                  >
                    <Calculator className="w-4 h-4 mr-1.5 text-slate-500" /> Good Faith Estimate
                  </Button>

                  <Button 
                    variant="outline" 
                    className="h-10 px-4 w-full sm:w-48 justify-center rounded-xl font-bold text-xs bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors shadow-2xs"
                    onClick={() => {
                      const npi = provider.provider?.npiNumber || provider.id;
                      const url = `${window.location.origin}/providers/${provider.name.toLowerCase().replace(/\s+/g, '-')}/${npi}`;
                      navigator.clipboard.writeText(url);
                      toast({ title: "Copied!", description: "Public profile URL copied to clipboard." });
                    }}
                  >
                    <Share className="w-4 h-4 mr-1.5 text-slate-500" /> Share
                  </Button>
                </div>
              </div>
            </div>

            {/* Main 2-Column Content Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* Left Main Content (lg:col-span-2) */}
              <div className="lg:col-span-2 space-y-6">
                <Tabs defaultValue="highlights" className="w-full">
                  <TabsList className="bg-transparent border-b border-slate-200 dark:border-slate-800 w-full justify-start h-auto rounded-none p-0 space-x-8 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    <TabsTrigger value="highlights" className="rounded-none bg-transparent py-4 px-0 font-bold text-slate-500 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-indigo-600 dark:data-[state=active]:border-indigo-400 transition-all">Highlights</TabsTrigger>
                    {isGroup && detail.linkedProviders && detail.linkedProviders.length > 0 && (
                      <TabsTrigger value="providers" className="rounded-none bg-transparent py-4 px-0 font-bold text-slate-500 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-indigo-600 dark:data-[state=active]:border-indigo-400 transition-all">Providers</TabsTrigger>
                    )}
                    <TabsTrigger value="about" className="rounded-none bg-transparent py-4 px-0 font-bold text-slate-500 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-indigo-600 dark:data-[state=active]:border-indigo-400 transition-all">About</TabsTrigger>
                    <TabsTrigger value="locations" className="rounded-none bg-transparent py-4 px-0 font-bold text-slate-500 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-indigo-600 dark:data-[state=active]:border-indigo-400 transition-all">Locations</TabsTrigger>
                    {user && isProviderAssociated && (
                      <TabsTrigger value="bundles" className="rounded-none bg-transparent py-4 px-0 font-bold text-slate-500 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-indigo-600 dark:data-[state=active]:border-indigo-400 transition-all">Bundles & Pricing</TabsTrigger>
                    )}
                    <TabsTrigger value="education" className="rounded-none bg-transparent py-4 px-0 font-bold text-slate-500 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-indigo-600 dark:data-[state=active]:border-indigo-400 transition-all">Credentials</TabsTrigger>
                    <TabsTrigger value="reviews" className="rounded-none bg-transparent py-4 px-0 font-bold text-slate-500 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-indigo-600 dark:data-[state=active]:border-indigo-400 transition-all">Reviews</TabsTrigger>
                  </TabsList>

                  <div className="mt-8">
                    <TabsContent value="highlights" className="mt-0 focus-visible:ring-0">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="flex gap-4 items-start">
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                            <Star className="w-5 h-5 fill-amber-500 text-amber-500" />
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900 dark:text-white">Highly recommended</h4>
                            <p className="text-slate-500 text-sm mt-1 leading-relaxed">98% of patients gave this provider 5 stars based on their recent visit.</p>
                          </div>
                        </div>

                        <div className="flex gap-4 items-start">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                            <Clock className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900 dark:text-white">Excellent wait time</h4>
                            <p className="text-slate-500 text-sm mt-1 leading-relaxed">100% of patients waited less than 30 minutes to be seen.</p>
                          </div>
                        </div>

                        {detail.yearsExperience && !isGroup ? (
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                              <Briefcase className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900 dark:text-white">Extensive experience</h4>
                              <p className="text-slate-500 text-sm mt-1 leading-relaxed">
                                Over <strong className="text-slate-700 dark:text-slate-300">{detail.yearsExperience} years</strong> of proven clinical expertise.
                              </p>
                            </div>
                          </div>
                        ) : null}

                        {detail.credentials && detail.credentials.some((c: any) => c.type === 'Education') && (
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                              <GraduationCap className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900 dark:text-white">Education</h4>
                              <div className="text-slate-500 text-sm mt-1 leading-relaxed">
                                {detail.credentials.filter((c: any) => c.type === 'Education').map((edu: any, idx: number) => (
                                  <div key={idx} className="mb-1 last:mb-0">
                                    <strong className="text-slate-700 dark:text-slate-300">{edu.title}</strong> from {edu.institution || 'a top medical institution'}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {detail.providerTypes && detail.providerTypes.length > 0 && (
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                              <Activity className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900 dark:text-white">Specialized care</h4>
                              <p className="text-slate-500 text-sm mt-1 leading-relaxed">
                                Providing high-quality services in <strong className="text-slate-700 dark:text-slate-300">{detail.providerTypes.join(', ')}</strong>.
                              </p>
                            </div>
                          </div>
                        )}

                        {detail.credentials && detail.credentials.some((c: any) => c.type === 'Achievement' || c.type === 'Certification' || c.type === 'Award') && (
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                              <Award className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900 dark:text-white">Awards & Certifications</h4>
                              <p className="text-slate-500 text-sm mt-1 leading-relaxed">Recognized for excellence in medical practice and board certifications.</p>
                            </div>
                          </div>
                        )}

                        {detail.accepting && (
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                              <CheckCircle className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900 dark:text-white">New patient appointments</h4>
                              <p className="text-slate-500 text-sm mt-1 leading-relaxed">Appointments are currently available for new patients.</p>
                            </div>
                          </div>
                        )}

                        {detail.medicareCharge && !isGroup && (
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                              <Shield className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900 dark:text-white">% of Fee Schedule</h4>
                              <p className="text-slate-500 text-sm mt-1 leading-relaxed">
                                <strong className="text-slate-700 dark:text-slate-300">{detail.medicareCharge.includes('%') ? detail.medicareCharge : detail.medicareCharge + '%'}</strong> Fee Schedule charge.
                              </p>
                            </div>
                          </div>
                        )}

                        {detail.telehealth && (
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 flex items-center justify-center shrink-0">
                              <Video className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900 dark:text-white">Telehealth available</h4>
                              <p className="text-slate-500 text-sm mt-1 leading-relaxed">This provider offers secure video visits.</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {detail.languages && detail.languages.length > 0 && (
                        <div className="mt-8 border-t border-slate-200 dark:border-slate-800 pt-8 flex gap-4 items-start">
                          <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 flex items-center justify-center shrink-0">
                            <Languages className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900 dark:text-white">Languages spoken</h4>
                            <p className="text-slate-500 text-sm mt-1">{detail.languages.join(", ")}</p>
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    {isGroup && detail.linkedProviders && detail.linkedProviders.length > 0 && (
                      <TabsContent value="providers" className="mt-0 focus-visible:ring-0">
                        <div className="grid grid-cols-1 gap-4">
                          {(detail.linkedProviders as any[]).map((lp) => (
                            <div key={lp.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors bg-white dark:bg-slate-900 cursor-pointer shadow-sm" onClick={() => window.open(`/member/providers/${lp.name.toLowerCase().replace(/\s+/g, '-')}/${lp.id}`, '_blank')}>
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 shrink-0 rounded-full bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-800/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold text-sm">
                                  {lp.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('')}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-slate-900 dark:text-white">{lp.name}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    {lp.headline || lp.specialty || 'General Practice'}
                                  </p>
                                  {lp.assignedLocations && lp.assignedLocations.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {lp.assignedLocations.map((locName: string, idx: number) => (
                                        <span key={idx} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800">
                                          <MapPin className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                          {locName}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-emerald-200/50 dark:border-emerald-800/50">
                                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                                {lp.medicareCharge ? (lp.medicareCharge.includes('%') ? lp.medicareCharge : lp.medicareCharge + '%') : "80%"} Fee Schedule
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    )}

                    <TabsContent value="about" className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm mt-0 focus-visible:ring-0">
                      <h3 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">Professional Synopsis</h3>
                      <div className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium whitespace-pre-wrap">
                        {detail.about || `${detail.name} is a board-certified ${detail.specialty} with over ${detail.yearsExperience} years of experience providing exceptional care to patients.`}
                      </div>
                    </TabsContent>

                    <TabsContent value="locations" className="space-y-4 mt-0 focus-visible:ring-0">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            <h3 className="font-bold text-slate-900 dark:text-white text-base">
                              Practice Locations ({detail.addresses?.length || 0})
                            </h3>
                          </div>
                          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
                            <button
                              type="button"
                              onClick={() => setLocationViewMode('grid')}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                locationViewMode === 'grid'
                                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                              }`}
                            >
                              <LayoutGrid className="w-3.5 h-3.5" />
                              Grid View
                            </button>
                            <button
                              type="button"
                              onClick={() => setLocationViewMode('list')}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                locationViewMode === 'list'
                                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                              }`}
                            >
                              <List className="w-3.5 h-3.5" />
                              Collapsible List
                            </button>
                          </div>
                        </div>

                        {detail.addresses && detail.addresses.length > 0 ? (
                          locationViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {detail.addresses.map((addr: any, idx: number) => (
                                <div key={addr.id || idx} className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between hover:border-indigo-200 dark:hover:border-indigo-900 transition-all">
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                                      <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)] animate-pulse shrink-0" />
                                        <h4 className="font-extrabold text-base text-slate-900 dark:text-white">
                                          Location #{idx + 1}
                                        </h4>
                                      </div>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                      <div className="flex items-start gap-3">
                                        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5">
                                          <MapPin className="w-4 h-4" />
                                        </div>
                                        <div>
                                          <p className="font-semibold text-slate-900 dark:text-white text-sm leading-tight">
                                            {addr.buildingNo && `${addr.buildingNo} `}{addr.street1}{addr.street2 && addr.street2.trim() !== addr.street1.trim() ? `, ${addr.street2}` : ''}
                                          </p>
                                          <p className="text-slate-500 dark:text-slate-400 text-[13px] mt-0.5 font-medium">{addr.city}, {addr.state} {addr.zipCode}</p>
                                        </div>
                                      </div>

                                      <div className="pt-2 space-y-2 border-t border-slate-100 dark:border-slate-800">
                                        {addr.phone && (
                                          <div className="flex items-center gap-3">
                                            <div className="p-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-400 shrink-0">
                                              <Phone className="w-3.5 h-3.5" />
                                            </div>
                                            <div className="text-xs">
                                              <span className="text-slate-400 mr-1">Location Phone:</span>
                                              <a href={`tel:${addr.phone}`} className="font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600">
                                                {addr.phone}
                                              </a>
                                            </div>
                                          </div>
                                        )}
                                        {addr.officePhone && addr.officePhone !== addr.phone && (
                                          <div className="flex items-center gap-3">
                                            <div className="p-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-400 shrink-0">
                                              <Building2 className="w-3.5 h-3.5" />
                                            </div>
                                            <div className="text-xs">
                                              <span className="text-slate-400 mr-1">Office Phone:</span>
                                              <a href={`tel:${addr.officePhone}`} className="font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600">
                                                {addr.officePhone}
                                              </a>
                                            </div>
                                          </div>
                                        )}
                                        {addr.fax && (
                                          <div className="flex items-center gap-3">
                                            <div className="p-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-400 shrink-0">
                                              <FileText className="w-3.5 h-3.5 text-slate-400" />
                                            </div>
                                            <div className="text-xs">
                                              <span className="text-slate-400 mr-1">Fax:</span>
                                              <span className="font-semibold text-slate-700 dark:text-slate-300">{addr.fax}</span>
                                            </div>
                                          </div>
                                        )}
                                        {addr.email && (
                                          <div className="flex items-center gap-3">
                                            <div className="p-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-400 shrink-0">
                                              <Mail className="w-3.5 h-3.5" />
                                            </div>
                                            <a href={`mailto:${addr.email}`} className="font-semibold text-xs text-slate-700 dark:text-slate-300 hover:text-indigo-600 truncate">
                                              {addr.email}
                                            </a>
                                          </div>
                                        )}
                                        {addr.website && (
                                          <div className="flex items-center gap-3">
                                            <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg text-indigo-600 dark:text-indigo-400 shrink-0">
                                              <Globe className="w-3.5 h-3.5" />
                                            </div>
                                            <a href={addr.website.startsWith('http') ? addr.website : `https://${addr.website}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-xs text-indigo-600 hover:underline truncate">
                                              {addr.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                            </a>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {detail.addresses.map((addr: any, idx: number) => {
                                const isExpanded = expandedLocations[idx] ?? (idx === 0);
                                const locTitle = `Location #${idx + 1}`;
                                const locSub = [addr.street1, addr.city, addr.state].filter(Boolean).join(", ");

                                return (
                                  <div key={addr.id || idx} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs transition-all">
                                    <div
                                      onClick={() => toggleLocationExpand(idx)}
                                      className="flex items-center justify-between p-5 cursor-pointer select-none bg-slate-50/50 hover:bg-slate-100/50 dark:bg-slate-800/40 dark:hover:bg-slate-800/80 transition-colors"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                                          <MapPin className="w-4 h-4" />
                                        </div>
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <h4 className="font-bold text-slate-900 dark:text-white text-base">
                                              {locTitle}
                                            </h4>
                                          </div>
                                          {locSub && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{locSub}</p>}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                                          {isExpanded ? "Collapse" : "Expand"}
                                        </span>
                                        {isExpanded ? (
                                          <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                        ) : (
                                          <ChevronDown className="w-4 h-4 text-slate-400" />
                                        )}
                                      </div>
                                    </div>

                                    {isExpanded && (
                                      <div className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-4 text-sm">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                          <div>
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Address Details</p>
                                            <p className="font-semibold text-slate-900 dark:text-white">
                                              {addr.buildingNo && `${addr.buildingNo} `}{addr.street1}
                                              {addr.street2 && <>, {addr.street2}</>}
                                            </p>
                                            <p className="text-slate-500 dark:text-slate-400">{addr.city}, {addr.state} {addr.zipCode}</p>
                                          </div>

                                          <div className="space-y-2">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Contact Information</p>
                                            {addr.phone && (
                                              <div className="flex items-center gap-2 text-xs">
                                                <Phone className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                                <span className="text-slate-500">Location Phone:</span>
                                                <a href={`tel:${addr.phone}`} className="font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600">
                                                  {addr.phone}
                                                </a>
                                              </div>
                                            )}
                                            {addr.officePhone && addr.officePhone !== addr.phone && (
                                              <div className="flex items-center gap-2 text-xs">
                                                <Building2 className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                                <span className="text-slate-500">Office Phone:</span>
                                                <a href={`tel:${addr.officePhone}`} className="font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600">
                                                  {addr.officePhone}
                                                </a>
                                              </div>
                                            )}
                                            {addr.fax && (
                                              <div className="flex items-center gap-2 text-xs">
                                                <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                <span className="text-slate-500">Fax:</span>
                                                <span className="font-semibold text-slate-700 dark:text-slate-300">{addr.fax}</span>
                                              </div>
                                            )}
                                            {addr.email && (
                                              <div className="flex items-center gap-2 text-xs">
                                                <Mail className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                                <a href={`mailto:${addr.email}`} className="font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600 truncate">
                                                  {addr.email}
                                                </a>
                                              </div>
                                            )}
                                            {addr.website && (
                                              <div className="flex items-center gap-2 text-xs">
                                                <Globe className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                                <a
                                                  href={addr.website.startsWith('http') ? addr.website : `https://${addr.website}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="font-semibold text-indigo-600 hover:underline truncate"
                                                >
                                                  {addr.website.replace(/^https?:\/\//, '')}
                                                </a>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )
                        ) : (
                          <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 text-slate-500 text-center font-medium">
                            No location information available.
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="bundles" className="space-y-4 mt-0 focus-visible:ring-0">
                      {(() => {
                        let displayedBundles = (detail.bundles || []).filter((b: any) => (b.status || 'active').toLowerCase() === 'active');
                        if (isGroup && selectedGroupProvider && selectedGroupProvider !== "any") {
                          displayedBundles = displayedBundles.filter((bundle: any) => {
                            if (bundle.type === 'GROUP' && (!bundle.participatingProviders || bundle.participatingProviders.isAll !== false)) return true;
                            if (bundle.participatingProviders?.isAll === true) return true;
                            if (bundle.type === `PROVIDER_${selectedGroupProvider}`) return true;
                            if (Array.isArray(bundle.participatingProviders?.providerIds)) {
                              return bundle.participatingProviders.providerIds.some((pId: any) => String(pId) === String(selectedGroupProvider));
                            }
                            if (Array.isArray(bundle.participatingProviders)) {
                              return bundle.participatingProviders.some((p: any) => String(p.providerId || p.id) === String(selectedGroupProvider));
                            }
                            return false;
                          });
                        }

                        return (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                              <div className="flex items-center gap-2">
                                <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                <h3 className="font-bold text-slate-900 dark:text-white text-base">
                                  Service Bundles & Pricing ({displayedBundles.length})
                                </h3>
                              </div>
                              <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
                                <button
                                  type="button"
                                  onClick={() => setBundleViewMode('grid')}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                    bundleViewMode === 'grid'
                                      ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                                  }`}
                                >
                                  <LayoutGrid className="w-3.5 h-3.5" />
                                  Grid View
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setBundleViewMode('list')}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                    bundleViewMode === 'list'
                                      ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                                  }`}
                                >
                                  <List className="w-3.5 h-3.5" />
                                  Collapsible Catalog
                                </button>
                              </div>
                            </div>

                            {displayedBundles.length > 0 ? (
                              bundleViewMode === 'grid' ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                  {displayedBundles.map((bundle: any, idx: number) => (
                                    <div key={bundle.id || idx} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group relative overflow-hidden">
                                      <div>
                                        <div className="flex items-center justify-between gap-2 mb-3">
                                          <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800/50">
                                              <Package className="w-3.5 h-3.5" /> Bundle Package
                                            </span>
                                            {bundle.discountPercent > 0 && (
                                              <span className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 font-bold text-[10px] uppercase tracking-wide">
                                                Save {bundle.discountPercent}%
                                              </span>
                                            )}
                                          </div>
                                          {user && user.role === 'MEMBER' && (
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={(e) => handleToggleWishlist(bundle.id, bundle.name, e)}
                                              className="h-8 w-8 rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                                              title={savedBundleIds.includes(bundle.id) ? "Remove from Saved Bundles" : "Save to Wishlist"}
                                            >
                                              <Bookmark className={`w-4 h-4 ${savedBundleIds.includes(bundle.id) ? "fill-indigo-600 text-indigo-600 dark:fill-indigo-400 dark:text-indigo-400" : ""}`} />
                                            </Button>
                                          )}
                                        </div>

                                        <Link href={`/bundle/${bundle.id}`}>
                                          <h4 className="font-bold text-base sm:text-lg text-slate-900 dark:text-white tracking-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors cursor-pointer line-clamp-1">
                                            {bundle.name}
                                          </h4>
                                        </Link>
                                        <p className="text-slate-500 dark:text-slate-400 text-xs mt-1 line-clamp-2 min-h-[2.25rem]">
                                          {bundle.description || "Comprehensive service package tailored for complete patient care."}
                                        </p>

                                        <div className="my-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-baseline gap-2">
                                          <span className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                                            ${bundle.finalPrice || bundle.totalPrice || bundle.standardPrice || 0}
                                          </span>
                                          {bundle.discountPercent > 0 && bundle.totalPrice && (
                                            <span className="text-xs text-slate-400 line-through font-medium">
                                              ${bundle.totalPrice}
                                            </span>
                                          )}
                                          <span className="text-[11px] text-slate-400 font-medium ml-auto">All-inclusive</span>
                                        </div>

                                        {bundle.cptCodes && bundle.cptCodes.length > 0 && (
                                          <div className="space-y-1.5 mb-4">
                                            <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                              Included Services ({bundle.cptCodes.length})
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                              {bundle.cptCodes.slice(0, 3).map((cpt: any) => (
                                                <span key={cpt.id || cpt.code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[11px] font-medium border border-slate-200/60 dark:border-slate-700/60 max-w-full truncate">
                                                  <span className="font-mono text-indigo-600 dark:text-indigo-400 font-bold">{cpt.code}</span>
                                                  <span className="truncate max-w-[100px]">{cpt.description}</span>
                                                </span>
                                              ))}
                                              {bundle.cptCodes.length > 3 && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-50 dark:bg-slate-800/60 text-slate-500 text-[11px] font-semibold border border-dashed border-slate-300 dark:border-slate-700">
                                                  +{bundle.cptCodes.length - 3} more
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {bundle.customItems && Array.isArray(bundle.customItems) && bundle.customItems.length > 0 && (
                                          <div className="space-y-1.5 mb-4">
                                            <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                              Additional Items ({bundle.customItems.length})
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                              {bundle.customItems.slice(0, 2).map((item: any, i: number) => (
                                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50/60 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-[11px] font-medium border border-indigo-100 dark:border-indigo-900/50">
                                                  {item.name}
                                                </span>
                                              ))}
                                              {bundle.customItems.length > 2 && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-50 text-slate-500 text-[11px] font-semibold">
                                                  +{bundle.customItems.length - 2} more
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Provided By Section */}
                                        {(() => {
                                          let providerBadges: { name: string }[] = [];
                                          let isAllGroupProviders = false;

                                          const linked = (detail as any)?.linkedProviders || (bundle as any)?.provider?.linkedProviders || [];
                                          const pProviders = bundle.participatingProviders;

                                          if (pProviders?.isAll === true || (!pProviders && bundle.type === 'GROUP')) {
                                            if (linked.length > 0) {
                                              isAllGroupProviders = true;
                                              providerBadges = linked.map((lp: any) => ({ name: lp.name || `${lp.user?.firstName || ''} ${lp.user?.lastName || ''}`.trim() || 'Doctor' }));
                                            } else {
                                              providerBadges = [{ name: detail?.name || 'All Practice Doctors' }];
                                            }
                                          } else if (Array.isArray(pProviders?.providerIds) && pProviders.providerIds.length > 0) {
                                            const ids = pProviders.providerIds.map(String);
                                            const matched = linked.filter((lp: any) => ids.includes(String(lp.providerId || lp.id)));
                                            if (matched.length > 0) {
                                              providerBadges = matched.map((lp: any) => ({ name: lp.name || `${lp.user?.firstName || ''} ${lp.user?.lastName || ''}`.trim() }));
                                            } else {
                                              providerBadges = ids.map((id: string) => ({ name: `Doctor #${id.slice(0, 4)}` }));
                                            }
                                          } else if (Array.isArray(pProviders) && pProviders.length > 0) {
                                            providerBadges = pProviders.map((p: any) => ({
                                              name: p.name || (p.user ? `${p.user.firstName} ${p.user.lastName}` : (p.firstName ? `${p.firstName} ${p.lastName}` : `Doctor`))
                                            }));
                                          } else if (bundle.type?.startsWith('PROVIDER_')) {
                                            const targetId = bundle.type.split('_')[1];
                                            const matchedLp = linked.find((lp: any) => String(lp.providerId) === String(targetId) || String(lp.id) === String(targetId));
                                            if (matchedLp) {
                                              providerBadges = [{ name: matchedLp.name }];
                                            } else {
                                              providerBadges = [{ name: detail?.name || 'Assigned Provider' }];
                                            }
                                          } else if (bundle.provider) {
                                            const pName = bundle.provider.name || (bundle.provider.user ? `${bundle.provider.user.firstName} ${bundle.provider.user.lastName}` : `${bundle.provider.firstName || ''} ${bundle.provider.lastName || ''}`.trim());
                                            if (pName) providerBadges = [{ name: pName }];
                                          } else {
                                            providerBadges = [{ name: detail?.name || 'Practice Provider' }];
                                          }

                                          if (providerBadges.length === 0) return null;

                                          return (
                                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                                              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                                <Stethoscope className="w-3 h-3 text-indigo-500" />
                                                <span>Provided By</span>
                                              </div>
                                              <div className="flex flex-wrap gap-1.5">
                                                {isAllGroupProviders ? (
                                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold border border-emerald-200/60 dark:border-emerald-800/50">
                                                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                                    <span>All Practice Doctors ({providerBadges.length})</span>
                                                  </span>
                                                ) : (
                                                  providerBadges.map((prov, i) => (
                                                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold border border-indigo-100 dark:border-indigo-900/50 max-w-full truncate">
                                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                                                      <span className="truncate">{prov.name}</span>
                                                    </span>
                                                  ))
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </div>

                                      <div className="pt-3 border-t border-slate-100 dark:border-slate-800 mt-auto flex items-center gap-2">
                                        <Link href={`/bundle/${bundle.id}`} className="flex-1">
                                          <Button variant="outline" className="w-full font-bold text-xs rounded-xl h-9 text-slate-700 dark:text-slate-200">
                                            View Details
                                          </Button>
                                        </Link>
                                        <Button
                                          onClick={() => setCheckoutBundle(bundle)}
                                          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs gap-1 h-9"
                                        >
                                          <CreditCard className="w-3.5 h-3.5" />
                                          <span>Checkout</span>
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {displayedBundles.map((bundle: any, idx: number) => {
                                    const bId = bundle.id || idx;
                                    const isExpanded = expandedBundles[bId] ?? (idx === 0);

                                    return (
                                      <div key={bId} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs transition-all">
                                        <div
                                          onClick={() => toggleBundleExpand(bId)}
                                          className="flex items-center justify-between p-4 sm:p-5 cursor-pointer select-none bg-slate-50/50 hover:bg-slate-100/50 dark:bg-slate-800/40 dark:hover:bg-slate-800/80 transition-colors"
                                        >
                                          <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-white shrink-0 shadow-xs">
                                              <Package className="w-5 h-5" />
                                            </div>
                                            <div className="min-w-0">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <h4 className="font-extrabold text-slate-900 dark:text-white text-base truncate">
                                                  {bundle.name}
                                                </h4>
                                                {bundle.discountPercent > 0 && (
                                                  <span className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 font-extrabold text-[10px] uppercase tracking-wide">
                                                    Save {bundle.discountPercent}%
                                                  </span>
                                                )}
                                              </div>
                                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                                                {bundle.description || "Comprehensive service package."}
                                              </p>
                                            </div>
                                          </div>

                                          <div className="flex items-center gap-4 shrink-0">
                                            {user && user.role === 'MEMBER' && (
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleToggleWishlist(bundle.id, bundle.name, e);
                                                }}
                                                className="h-8 w-8 rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                                                title={savedBundleIds.includes(bundle.id) ? "Remove from Saved Bundles" : "Save to Wishlist"}
                                              >
                                                <Bookmark className={`w-4 h-4 ${savedBundleIds.includes(bundle.id) ? "fill-indigo-600 text-indigo-600 dark:fill-indigo-400 dark:text-indigo-400" : ""}`} />
                                              </Button>
                                            )}
                                            <div className="text-right">
                                              <span className="text-lg font-black text-slate-900 dark:text-white block leading-tight">
                                                ${bundle.finalPrice || bundle.totalPrice || bundle.standardPrice || 0}
                                              </span>
                                              {bundle.discountPercent > 0 && bundle.totalPrice && (
                                                <span className="text-[11px] text-slate-400 line-through block">
                                                  ${bundle.totalPrice}
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-1.5 pl-2 border-l border-slate-200 dark:border-slate-700">
                                              <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hidden sm:inline">
                                                {isExpanded ? "Collapse" : "Expand"}
                                              </span>
                                              {isExpanded ? (
                                                <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                              ) : (
                                                <ChevronDown className="w-4 h-4 text-slate-400" />
                                              )}
                                            </div>
                                          </div>
                                        </div>

                                        {isExpanded && (
                                          <div className="p-5 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-4 text-sm bg-white dark:bg-slate-900">
                                            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
                                              {bundle.description || "Comprehensive service package tailored for complete patient care."}
                                            </p>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                              {bundle.cptCodes && bundle.cptCodes.length > 0 && (
                                                <div className="space-y-2">
                                                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                                    Included CPT Services ({bundle.cptCodes.length})
                                                  </div>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {bundle.cptCodes.map((cpt: any) => (
                                                      <span key={cpt.id || cpt.code} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium border border-slate-200/60 dark:border-slate-700/60">
                                                        <span className="font-mono text-indigo-600 dark:text-indigo-400 font-bold">{cpt.code}</span>
                                                        <span>{cpt.description}</span>
                                                      </span>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}

                                              {bundle.customItems && Array.isArray(bundle.customItems) && bundle.customItems.length > 0 && (
                                                <div className="space-y-2">
                                                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                                    Additional Package Items ({bundle.customItems.length})
                                                  </div>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {bundle.customItems.map((item: any, i: number) => (
                                                      <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-lg bg-indigo-50/60 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-xs font-medium border border-indigo-100 dark:border-indigo-900/50">
                                                        {item.name}
                                                      </span>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                            </div>

                                            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4">
                                              <div className="text-xs text-slate-500 font-medium">
                                                All-inclusive pricing guarantees no hidden charges.
                                              </div>
                                              <div className="flex items-center gap-2">
                                                <Link href={`/bundle/${bundle.id}`}>
                                                  <Button variant="outline" className="font-bold text-xs rounded-xl h-9 px-3.5 text-slate-700 dark:text-slate-200">
                                                    View Details
                                                  </Button>
                                                </Link>
                                                <Button
                                                  onClick={() => setCheckoutBundle(bundle)}
                                                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs gap-1.5 px-4 h-9"
                                                >
                                                  <CreditCard className="w-3.5 h-3.5" />
                                                  <span>Checkout</span>
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )
                            ) : (
                              <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 text-slate-500 text-center font-medium">
                                No service bundles are currently listed for this provider.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="education" className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm mt-0 focus-visible:ring-0">
                      <div>
                        {detail.credentials && detail.credentials.length > 0 ? (
                          Object.entries(
                            detail.credentials.reduce((acc: any, cred: any) => {
                              const t = cred.type || 'Other';
                              if (!acc[t]) acc[t] = [];
                              acc[t].push(cred);
                              return acc;
                            }, {})
                          ).map(([type, creds]: [string, any], typeIndex) => (
                            <div key={typeIndex} className="mb-10 last:mb-0">
                              <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4 pb-2 border-b border-slate-100 dark:border-slate-800">{type}</h4>
                              <div className="space-y-4">
                                {creds.map((cred: any, idx: number) => {
                                  return (
                                    <div key={cred.id || idx} className="flex gap-4 items-start">
                                      <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 font-bold text-sm">
                                        {type === 'Education' ? <GraduationCap className="w-4 h-4" /> : <Award className="w-4 h-4" />}
                                      </div>
                                      <div>
                                        <div className="font-bold text-slate-900 dark:text-white">{cred.title}</div>
                                        <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                          {cred.institution && <span>{cred.institution}</span>}
                                          {cred.institution && cred.year && <span> · </span>}
                                          {cred.year && <span>{cred.year}</span>}
                                        </div>
                                        {cred.description && (
                                          <div className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-1">{cred.description}</div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-slate-500 font-medium text-center">No credentials available.</div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="reviews" className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm mt-0 focus-visible:ring-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 md:p-8 border-b border-slate-200 dark:border-slate-800 gap-4">
                        <div className="flex items-center gap-4">
                          <h3 className="text-xl font-bold">Patient Reviews</h3>
                          <div className="flex items-center gap-2">
                            <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
                            <span className="text-2xl font-black">{avgRating.toFixed(1)}</span>
                            <span className="text-slate-500 font-medium">({reviewCount})</span>
                          </div>
                        </div>
                        {provider?.provider?.practiceType !== "group" && (
                          <Select
                            value={reviewFilter}
                            onValueChange={(val) => setReviewFilter(val as any)}
                          >
                            <SelectTrigger className="w-[180px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-sm rounded-xl px-3 py-2 text-slate-700 dark:text-slate-300 font-medium focus:ring-2 focus:ring-indigo-500/20 shadow-sm">
                              <SelectValue placeholder="All Reviews" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-lg">
                              <SelectItem value="all" className="py-2 cursor-pointer font-medium">All Reviews</SelectItem>
                              <SelectItem value="individual" className="py-2 cursor-pointer font-medium">Individual Reviews</SelectItem>
                              <SelectItem value="group" className="py-2 cursor-pointer font-medium">Group Reviews</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      <div className="p-6 md:p-8">
                        {realReviews && realReviews.length > 0 ? (
                          <div className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {realReviews.map((r: any) => (
                              <div key={r.id} className="py-5 first:pt-0 last:pb-0">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="font-bold text-sm text-slate-900 dark:text-white">{r.member?.user?.displayName || "Anonymous"}</p>
                                  <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200 dark:fill-slate-700 dark:text-slate-700"}`} />)}
                                  </div>
                                </div>
                                {r.comment && <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{r.comment}</p>}
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 font-medium">{new Date(r.createdAt).toLocaleDateString()}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="py-12 text-center">
                            <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                            <h3 className="text-lg font-medium text-slate-900">No reviews yet</h3>
                            <p className="text-slate-500">Check back later for patient feedback.</p>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </div>
                </Tabs>
              </div>

              {/* Right Sidebar: Practice Info & Hours (lg:col-span-1) */}
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-6 sticky top-24">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <h3 className="font-bold text-base text-slate-900 dark:text-white">Practice Information</h3>
                    </div>
                    {detail.addresses && detail.addresses.length > 0 ? (
                      <div className="text-sm space-y-2 text-slate-600 dark:text-slate-400">
                        <p className="font-semibold text-slate-900 dark:text-white">
                          {detail.addresses[0].street1}{detail.addresses[0].street2 ? `, ${detail.addresses[0].street2}` : ''}
                        </p>
                        <p>{detail.addresses[0].city}, {detail.addresses[0].state} {detail.addresses[0].zipCode}</p>

                        {detail.addresses[0].phone && (
                          <div className="flex items-center gap-2 pt-1 text-slate-700 dark:text-slate-300">
                            <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                            <a href={`tel:${detail.addresses[0].phone}`} className="hover:text-indigo-600 font-medium">
                              {detail.addresses[0].phone}
                            </a>
                          </div>
                        )}
                        {detail.addresses[0].email && (
                          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                            <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                            <a href={`mailto:${detail.addresses[0].email}`} className="hover:text-indigo-600 font-medium truncate">
                              {detail.addresses[0].email}
                            </a>
                          </div>
                        )}
                        {detail.addresses[0].website && (
                          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                            <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                            <a href={detail.addresses[0].website.startsWith('http') ? detail.addresses[0].website : `https://${detail.addresses[0].website}`} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600 font-medium truncate">
                              {detail.addresses[0].website.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 font-medium">Contact provider office for location details.</p>
                    )}
                  </div>

                  <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl text-emerald-600 dark:text-emerald-400">
                        <Clock className="w-5 h-5" />
                      </div>
                      <h3 className="font-bold text-base text-slate-900 dark:text-white">Office Hours</h3>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center text-slate-600 dark:text-slate-400">
                        <span>Monday - Friday</span>
                        <span className="font-semibold text-slate-900 dark:text-white">9:00 AM - 5:00 PM</span>
                      </div>
                      <div className="flex justify-between items-center text-slate-600 dark:text-slate-400">
                        <span>Saturday</span>
                        <span className="font-semibold text-slate-900 dark:text-white">10:00 AM - 2:00 PM</span>
                      </div>
                      <div className="flex justify-between items-center text-slate-600 dark:text-slate-400">
                        <span>Sunday</span>
                        <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded font-bold uppercase text-[10px]">Closed</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 dark:border-slate-800 pt-5 space-y-2.5">
                    {detail.accepting && (
                      <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 p-2.5 rounded-xl border border-emerald-200/60 dark:border-emerald-800/50">
                        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span>Accepting New Patients</span>
                      </div>
                    )}
                    {detail.telehealth && (
                      <div className="flex items-center gap-2 text-xs font-semibold text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 p-2.5 rounded-xl border border-indigo-200/60 dark:border-indigo-800/50">
                        <Video className="w-4 h-4 text-indigo-500 shrink-0" />
                        <span>Telehealth Consultations Available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
        </div>
      </main>

      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden border-0 shadow-2xl">
          <div className="bg-indigo-600 p-6 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold text-white mb-2">Request Sent!</DialogTitle>
            <DialogDescription className="text-indigo-100 text-base">
              Your appointment request has been successfully sent to the provider.
            </DialogDescription>
          </div>
          <div className="p-6 bg-white dark:bg-slate-900 text-center space-y-4">
            {createdVerificationCode && (
              <div className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 flex flex-col items-center">
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">Your Verification Code</span>
                <span className="text-3xl font-mono font-extrabold text-indigo-900 dark:text-indigo-100 tracking-widest mt-1 bg-white dark:bg-slate-900 px-5 py-2 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                  {createdVerificationCode}
                </span>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 font-medium">
                  Please save this code. Share it with your provider when you visit for your appointment.
                </p>
              </div>
            )}
            <p className="text-slate-600 dark:text-slate-300 font-medium">
              We will notify you as soon as the provider confirms your booking.
            </p>
            <DialogFooter className="sm:justify-center">
              <DialogClose asChild>
                <Button className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-8 py-2.5 h-auto font-semibold">
                  Got it, thanks!
                </Button>
              </DialogClose>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEstimatorModal} onOpenChange={(open) => {
        setShowEstimatorModal(open);
        if (!open) {
          setSelectedCptCodes([]);
          setSearchQuery("");
          setEstimatePatientNotes("");
        }
      }}>
        <DialogContent className="sm:max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Good Faith Estimate (GFE)</DialogTitle>
            <DialogDescription>
              Select CPT codes to estimate the cost based on {detail.name}'s Fee Schedule percentage ({detail.medicareCharge ? detail.medicareCharge : "100%"}).
            </DialogDescription>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search procedures by code or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
              />
            </div>
          </DialogHeader>

          <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-2 my-2">
            {masterCptCodes?.items?.filter((cpt: any) =>
              cpt.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
              cpt.description.toLowerCase().includes(searchQuery.toLowerCase())
            ).map((cpt: any) => {
              const isSelected = selectedCptCodes.some(c => c.id === cpt.id);
              const medicareMultiplier = detail.medicareCharge ? parseFloat(detail.medicareCharge.replace('%', '')) / 100 : 1;
              const payable = cpt.standardPrice * (1 + medicareMultiplier);
              return (
                <div
                  key={cpt.id}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-colors flex items-center justify-between ${isSelected ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedCptCodes(prev => prev.filter(c => c.id !== cpt.id));
                    } else {
                      setSelectedCptCodes(prev => [...prev, cpt]);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded flex items-center justify-center border ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 dark:border-slate-600'}`}>
                      {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-slate-900 dark:text-white">{cpt.code} - {cpt.description}</div>
                      <div className="text-xs text-slate-500">
                        {typeof cpt.category === 'object' && cpt.category !== null
                          ? ((cpt.category as any)?.name || (cpt.category as any)?.code || "")
                          : String(cpt.category || "")}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-slate-900 dark:text-white">${payable.toFixed(2)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-indigo-500" /> Patient Notes / Special Instructions for Provider (Optional)
              </label>
              <Input
                type="text"
                placeholder="e.g. Requesting out-of-pocket pricing review or care bundle quote..."
                value={estimatePatientNotes}
                onChange={(e) => setEstimatePatientNotes(e.target.value)}
                className="text-xs rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              />
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-xs text-slate-500 font-medium">Estimated Payable Amount</div>
                <div className="text-2xl font-black text-slate-900 dark:text-white">
                  ${selectedCptCodes.reduce((sum, cpt) => {
                    const medicareMultiplier = detail.medicareCharge ? parseFloat(detail.medicareCharge.replace('%', '')) / 100 : 1;
                    return sum + (cpt.standardPrice * (1 + medicareMultiplier));
                  }, 0).toFixed(2)}
                </div>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  className="rounded-xl px-4 text-xs font-semibold flex-1 sm:flex-none border-slate-200 dark:border-slate-700"
                  disabled={selectedCptCodes.length === 0 || createEstimate.isPending}
                  onClick={() => {
                    const medicareMultiplier = detail.medicareCharge ? parseFloat(detail.medicareCharge.replace('%', '')) / 100 : 1;
                    const totalCost = selectedCptCodes.reduce((sum, cpt) => sum + (cpt.standardPrice * (1 + medicareMultiplier)), 0);

                    createEstimate.mutate({
                      data: {
                        procedure: selectedCptCodes.map(c => c.description).join(", ") || "Custom Estimate",
                        cptCodes: selectedCptCodes.map(c => c.code).join(", "),
                        providerName: detail.name,
                        providerType: detail.headline || detail.specialty || "Specialist",
                        providerId: detail.id,
                        patientResponsibility: totalCost,
                        estimatedCost: totalCost,
                        status: "SAVED",
                        patientNotes: estimatePatientNotes || undefined,
                      }
                    }, {
                      onSuccess: () => {
                        toast({ title: "Draft Estimate Saved", description: "You can view and submit this estimate anytime from your Saved Estimates." });
                        setShowEstimatorModal(false);
                        setSelectedCptCodes([]);
                        setEstimatePatientNotes("");
                      },
                      onError: (err: any) => {
                        toast({ variant: "destructive", title: "Error", description: err.message || "Failed to save draft estimate." });
                      }
                    });
                  }}
                >
                  Save Draft
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-5 text-xs font-bold gap-1.5 flex-1 sm:flex-none shadow-md shadow-indigo-600/20"
                  disabled={selectedCptCodes.length === 0 || createEstimate.isPending}
                  onClick={() => {
                    const medicareMultiplier = detail.medicareCharge ? parseFloat(detail.medicareCharge.replace('%', '')) / 100 : 1;
                    const totalCost = selectedCptCodes.reduce((sum, cpt) => sum + (cpt.standardPrice * (1 + medicareMultiplier)), 0);

                    createEstimate.mutate({
                      data: {
                        procedure: selectedCptCodes.map(c => c.description).join(", ") || "Custom Estimate",
                        cptCodes: selectedCptCodes.map(c => c.code).join(", "),
                        providerName: detail.name,
                        providerType: detail.headline || detail.specialty || "Specialist",
                        providerId: detail.id,
                        patientResponsibility: totalCost,
                        estimatedCost: totalCost,
                        status: "SUBMITTED",
                        patientNotes: estimatePatientNotes || undefined,
                      }
                    }, {
                      onSuccess: () => {
                        toast({
                          title: "Estimate Submitted to Provider!",
                          description: `Your estimate of $${totalCost.toFixed(2)} has been sent to ${detail.name} for service review.`
                        });
                        setShowEstimatorModal(false);
                        setSelectedCptCodes([]);
                        setEstimatePatientNotes("");
                      },
                      onError: (err: any) => {
                        toast({ variant: "destructive", title: "Submission Failed", description: err.message || "Could not submit estimate to provider." });
                      }
                    });
                  }}
                >
                  <Send className="w-3.5 h-3.5" /> Submit to Provider
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Provider Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl p-6 overflow-hidden">
          <DialogHeader className="text-left">
            <div className="w-12 h-12 bg-amber-50 dark:bg-amber-950/60 rounded-2xl flex items-center justify-center mb-3 text-amber-600 dark:text-amber-400">
              <Stethoscope className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
              Invite {detail?.name || 'Provider'} to Join StraightDoc
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
              {detail?.name || 'This provider'} is currently listed in our healthcare directory but is not yet associated with the StraightDoc portal.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 my-2 rounded-2xl bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800 text-amber-900 dark:text-amber-200 text-xs leading-relaxed font-medium">
            "Tell your provider you want transparent pricing and seamless direct appointments with them on StraightDoc. We will send an invitation on your behalf to help onboard them to our network!"
          </div>

          <form onSubmit={handleSendInvitation} className="space-y-4 mt-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Your Name (Optional)</label>
              <Input
                placeholder="e.g. Jane Doe"
                value={invitePatientName}
                onChange={(e) => setInvitePatientName(e.target.value)}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Message / Note to Provider (Optional)</label>
              <Input
                placeholder="I would love to book direct appointments with your practice on StraightDoc!"
                value={inviteNote}
                onChange={(e) => setInviteNote(e.target.value)}
                className="rounded-xl"
              />
            </div>

            <DialogFooter className="sm:justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" className="rounded-xl" onClick={() => setShowInviteModal(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold gap-2"
                disabled={inviteProviderMutation.isPending}
              >
                {inviteProviderMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending Invitation...</>
                ) : (
                  <><Mail className="w-4 h-4" /> Send Invitation</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <QuickBundleCheckoutModal
        open={Boolean(checkoutBundle)}
        onOpenChange={(open) => !open && setCheckoutBundle(null)}
        bundle={checkoutBundle}
        providerInfo={provider}
      />
    </div>
  );
}
